/** @jsxRuntime classic */
/** @jsx jsx */
import dayjs from 'dayjs';
import React, { Fragment } from 'react';
import { jsx, css } from '@emotion/react';
import { SectionContainer } from '../sectionContainer';
import { Table } from '../../table';
import { AlignType, IColumn } from '../../table/types';
import { useDetectPrint } from '../../../hooks/useIsPrint';
import { EInvoiceVariant, IInvoiceTemplateLabels } from '../../../organisms/invoiceTemplate/types';
import { EInvoiceAction, EInvoicingTabs } from '../types';
import { SkeletonWrapper } from '../../../components/skeletonWrapper';
import { Typography } from '../../../atoms';
import { formatDateTimeTz } from '../utils/date-formatting.util';
import { ETypography, ETypographyColor } from '../../../types';
import { flexColCss, gapCss } from '../../../styles/utils';
import { FutureInstallmentDto } from '@bondsports/types';

const HIDE_SCHEDULED_PAYMENTS_VARIANTS = [EInvoiceVariant.PAYMENT_PRINT, EInvoiceVariant.PAYMENT];
const formatDateUtil  = formatDateTimeTz().formatDate;

export const ScheduledPayments = ({
	labels,
	additional,
	isLoading,
	activeTab,
	variant,
	state,
}: {
	labels: IInvoiceTemplateLabels;
	additional: any;
	isLoading: boolean;
	activeTab: string;
	additionalInfoRow?: string;
	state: any;
	variant: EInvoiceVariant;
	onAction: (action: EInvoiceAction, value: any) => void;
}) => {
	const isPrint = useDetectPrint();
	const rowsToDisplay: FutureInstallmentDto[] = additional.scheduledPayments;
	const tableLabels = labels.scheduledSection.table.columns;

	const columns = [
		{
			id: 'originalPlannedDate',
			label: tableLabels.date,
			type: 'custom',
			styling: { align: AlignType.LEFT },
			component: (state: string) => {
				return (
					<SkeletonWrapper isLoading={isLoading} minHeight={6} minWidth={50}>
						<Typography type={ETypography.body2} color={ETypographyColor.primary}>
						{formatDateUtil(state)}
						</Typography>
					</SkeletonWrapper>
				);
			},
		},
		{
			id: 'price',
			label: tableLabels.amount,
			type: 'currency',
			styling: { align: AlignType.LEFT },
		},
		{
			id: 'fee',
			type: isPrint ? 'fee' : 'feeWithIcon',
			label: tableLabels.fees,
			styling: { align: AlignType.LEFT },
		},
		{
			id: 'totalPrice',
			label: tableLabels.total,
			type: 'currency',
			styling: { align: AlignType.LEFT },
		},
		{
			id: 'method',
			label: tableLabels.method,
			type: 'paymentMethodWithIcon',
			styling: { align: AlignType.LEFT },
		},
		{
			id: 'status',
			label: tableLabels.status,
			type: isPrint ? 'status' : 'statusWithIcon',
			styling: { align: AlignType.LEFT },
		},
	];
	const tableContainerCss = css`
		${flexColCss};
		${ gapCss(12)};
		height: 100%;
	`;
	return (
		<Fragment>
			{activeTab === EInvoicingTabs.SCHEDULE && !HIDE_SCHEDULED_PAYMENTS_VARIANTS.includes(variant) && !isPrint ? (
				<Fragment />
			) : (
				<Fragment>
					{rowsToDisplay?.length > 0 && (
						<SectionContainer title={labels.scheduledSection.title}>
							<div css={tableContainerCss}>
								<Table<any>
									maxHeight={isPrint ? -1 : undefined}
									columns={columns as IColumn[]}
									rows={rowsToDisplay}
									pagination={false}
									isLoading={isLoading}
									isHover={false}
								/>
							</div>
						</SectionContainer>
					)}
				</Fragment>
			)}
		</Fragment>
	);
};
